import { c as _c } from "react/compiler-runtime";
import type { CoursePageSection, FullCourseLevel, FullCourseOffering, FullCourseOfferings } from "@repo/sanity-utils/query-types";
import type { CampusLocation } from "@repo/actions/AcademyApi/GetCampuses";
import { Typography } from "@repo/ui/aops-components/typography";
import { CourseButton } from "@/app/components/CourseLevelPage/CourseButton/CourseButton";
import { CourseTag } from "@/app/components/CourseLevelPage/CourseTag/CourseTag";
import { formatOfferingPrice } from "@/lib/offeringPrice";
import type { InPersonBillingType } from "@/lib/learningFormatData";
import { isBeastAcademyModalitySlug } from "@/lib/beastAcademy";
import { BEAST_ACADEMY_MODALITY_SLUG } from "@/lib/const";
import { sendDiscoveryEventBeacon } from "@/lib/discoveryEventBeacon";
import { getBeastAcademyPrimaryCTAContent, getOfferingPrimaryCTAContent, resolvePrimaryCTAButtonTrackingEvent, type Modality } from "@/app/components/CourseLevelPage/CourseCTACard/PrimaryCTA/PrimaryCTAUtils";
import styles from "./PrimaryCTA.module.css";
interface PrimaryCTAProps {
  /** Null for modalities with no course offerings, such as Beast Academy. */
  offering: FullCourseOffering | null;
  /** Selected modality, used to resolve offering-less variants. */
  modality?: Modality;
  courseOfferings: FullCourseOfferings;
  courseTitle: string;
  courseSlug: string;
  coursePageSection?: NonNullable<CoursePageSection>;
  campuses: CampusLocation[];
  campusCoursePrices: Readonly<Record<string, number>>;
  campusIdForUrl: string | null;
  consultationUrl: string;
  priceDisplayMode: string | null | undefined;
  selectedCampusId: string | null;
  sequenceType?: NonNullable<FullCourseLevel>["sequenceType"];
  inPersonBillingType?: InPersonBillingType | null;
}
function getPrimaryCTAButtonId(slug: string) {
  if (slug === "book-consultation") {
    return "book-consultation-primary-button";
  }
  if (slug === BEAST_ACADEMY_MODALITY_SLUG) {
    return "beast-academy-primary-button";
  }
  return `view-schedule-${slug}-button`;
}

/**
 * This is the top section of the CTA Card
 */
export function PrimaryCTA(t0) {
  const $ = _c(28);
  const {
    offering,
    modality,
    courseOfferings,
    courseTitle,
    courseSlug,
    coursePageSection,
    campuses,
    campusCoursePrices,
    campusIdForUrl,
    consultationUrl,
    priceDisplayMode,
    selectedCampusId,
    sequenceType,
    inPersonBillingType
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== campusCoursePrices || $[1] !== campusIdForUrl || $[2] !== campuses || $[3] !== consultationUrl || $[4] !== courseOfferings || $[5] !== coursePageSection?.keyedCopy || $[6] !== courseSlug || $[7] !== courseTitle || $[8] !== inPersonBillingType || $[9] !== modality || $[10] !== offering || $[11] !== priceDisplayMode || $[12] !== selectedCampusId || $[13] !== sequenceType) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const keyedCopy = coursePageSection?.keyedCopy ?? [];
      const content = isBeastAcademyModalitySlug(modality?.slug?.current) ? getBeastAcademyPrimaryCTAContent(modality, keyedCopy) : offering ? getOfferingPrimaryCTAContent({
        offering,
        courseOfferings,
        courseTitle,
        keyedCopy,
        campuses,
        campusCoursePrices,
        campusIdForUrl,
        consultationUrl,
        priceDisplayMode,
        selectedCampusId,
        sequenceType,
        inPersonBillingType
      }) : null;
      if (!content) {
        t6 = null;
        break bb0;
      }
      t4 = styles.primaryCTA;
      t5 = <div className={styles.ctaMessage}><CourseTag>{content.stepTag}</CourseTag><Typography as="h3" variant="h3">{content.header}</Typography>{content.body && <Typography as="p" variant="body">{content.body}</Typography>}</div>;
      t1 = styles.ctaAction;
      t2 = <div className={styles.ctaActionMessage}><Typography as="p" variant="eyebrow" className={styles.tuitionLabel}>{content.tuitionHeader}</Typography><div className={styles.priceRow}><Typography as="p" variant="h2" className={styles.price}>{content.tuition ? formatOfferingPrice(content.tuition) : ""}</Typography><div className={styles.priceDetails}><Typography as="p" variant="body" className={styles.billingPeriod}>{content.tuitionLabel}</Typography><Typography as="p" variant="bodySmall" className={styles.tuitionSubLabel}>{content.tuitionSubLabel}</Typography></div></div></div>;
      t3 = content.buttons.length > 0 && <div className={styles.buttons}>{content.buttons.map(t7 => {
          const {
            href,
            label,
            slug
          } = t7;
          return <CourseButton key={slug} id={getPrimaryCTAButtonId(slug)} href={href} label={label} className={styles.button} target="_blank" onClick={() => {
            if (!href) {
              return;
            }
            const trackingEvent = resolvePrimaryCTAButtonTrackingEvent(slug, courseSlug, offering);
            if (trackingEvent) {
              sendDiscoveryEventBeacon(trackingEvent.event, trackingEvent.properties);
            }
          }} />;
        })}</div>;
    }
    $[0] = campusCoursePrices;
    $[1] = campusIdForUrl;
    $[2] = campuses;
    $[3] = consultationUrl;
    $[4] = courseOfferings;
    $[5] = coursePageSection?.keyedCopy;
    $[6] = courseSlug;
    $[7] = courseTitle;
    $[8] = inPersonBillingType;
    $[9] = modality;
    $[10] = offering;
    $[11] = priceDisplayMode;
    $[12] = selectedCampusId;
    $[13] = sequenceType;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t1 = $[14];
    t2 = $[15];
    t3 = $[16];
    t4 = $[17];
    t5 = $[18];
    t6 = $[19];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[20] !== t1 || $[21] !== t2 || $[22] !== t3) {
    t7 = <div className={t1}>{t2}{t3}</div>;
    $[20] = t1;
    $[21] = t2;
    $[22] = t3;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t4 || $[25] !== t5 || $[26] !== t7) {
    t8 = <div className={t4}>{t5}{t7}</div>;
    $[24] = t4;
    $[25] = t5;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  return t8;
}